import { useCallback, useEffect, useState } from "react";
import { CubeTransparent, FileXls, FilePdf, Image as ImageIcon, Camera, DownloadSimple, FolderOpen, Blueprint, Cube } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtErr } from "../../lib/api";
import { download, exportGLB, renderCheckView, renderCurrentView, renderStandardViews } from "../../engine/viewer/exporters";

const KB = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function Row({ icon: Icon, title, note, actions }) {
  return (
    <div className="flex items-start gap-3 border-b border-[var(--c-border)]/60 px-3 py-2.5 last:border-0">
      <Icon size={16} className="mt-0.5 shrink-0 text-[var(--c-accent)]" />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-[var(--c-text)]">{title}</div>
        <div className="text-[11px] leading-snug text-[var(--c-muted)]">{note}</div>
      </div>
      <div className="flex shrink-0 flex-wrap justify-end gap-1.5">{actions}</div>
    </div>
  );
}

const Btn = ({ children, ...p }) => (
  <button {...p}
    className="flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-border)] px-2.5 text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)] hover:text-[var(--c-accent)] disabled:opacity-40">
    {children}
  </button>
);

/** Phase 4 — every artifact a reviewer receives, downloaded and filed in the DMS. */
export default function ExportsPanel({ projectId, model, compliance, viewerRef, busy, setBusy }) {
  const [files, setFiles] = useState(null);
  const [step, setStep] = useState(null);
  const meta = model?.meta || {};
  const tag = meta.tag || "model";
  const stamp = `${tag}_${meta.id || "design"}_${meta.revision || "r0"}`;

  const refresh = useCallback(() => {
    if (!projectId) return;
    api.get(`/engine/${projectId}/exports`).then(({ data }) => setFiles(data)).catch(() => setFiles(null));
  }, [projectId]);
  useEffect(refresh, [refresh]);

  const ctrl = () => {
    const c = viewerRef.current?.controller?.();
    if (!c) toast.error("The 3D view is still starting up — try again in a moment");
    return c;
  };

  const fileIt = async (blob, kind, ext, notes) => {
    const fd = new FormData();
    fd.append("file", blob, `${stamp}.${ext}`);
    fd.append("kind", kind);
    fd.append("notes", notes);
    const { data } = await api.post(`/engine/${projectId}/export/artifact`, fd);
    return data;
  };

  const glb = async (full) => {
    const c = ctrl();
    if (!c) return;
    try {
      setBusy(true); setStep("Writing the GLB…");
      const blob = await exportGLB(c, {
        full,
        meta: {
          name: `${tag} — ${meta.title || "design model"}`,
          design_model: meta.id, revision: meta.revision, schema: model.schema,
          project: files?.project_code || "", equipment_tag: tag, spec_ref: meta.spec_ref,
          datum: meta.datum, exported: new Date().toISOString(),
          contents: full ? "complete model" : "as shown on screen",
        },
      });
      download(blob, `${stamp}${full ? "" : "_as-shown"}.glb`);
      const filed = await fileIt(blob, full ? "model" : "model-as-shown", "glb",
        full ? "Complete design model (glTF binary)" : "Design model as shown on screen");
      toast.success(`GLB ${KB(blob.size)} downloaded and filed as ${filed.filename}`);
      refresh();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); setStep(null); }
  };

  const views = async () => {
    const c = ctrl();
    if (!c) return;
    try {
      setBusy(true); setStep("Rendering the standard views…");
      const shots = await renderStandardViews(c, { width: 2400 });
      const fd = new FormData();
      shots.forEach((s) => fd.append(s.name, s.blob, `${s.name}.png`));
      const flagged = (compliance?.rows || [])
        .filter((r) => (r.status === "FAIL" || r.status === "SPEC ISSUE") && r.parts?.length)
        .slice(0, 10);
      for (const r of flagged) {
        setStep(`Rendering ${r.check_id}…`);
        const blob = await renderCheckView(c, r.parts, { width: 1600 });
        if (blob) fd.append(`check-${r.check_id}`, blob, `${r.check_id}.png`);
      }
      setStep("Filing the views…");
      const { data } = await api.post(`/engine/${projectId}/views`, fd);
      shots.forEach((s) => download(s.blob, `${stamp}_${s.name}.png`));
      toast.success(`${shots.length} view(s)${flagged.length ? ` + ${flagged.length} check view(s)` : ""} rendered, downloaded and filed`);
      setFiles((f) => ({ ...(f || {}), views: data.saved }));
      refresh();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); setStep(null); }
  };

  const snap = async () => {
    const c = ctrl();
    if (!c) return;
    try {
      setBusy(true); setStep("Capturing the view…");
      const blob = await renderCurrentView(c, { width: 2400 });
      download(blob, `${stamp}_capture.png`);
      const filed = await fileIt(blob, "capture", "png", "Captured view of the design model");
      toast.success(`Capture filed as ${filed.filename}`);
      refresh();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); setStep(null); }
  };

  const SERVER = {
    pdf: ["compliance.pdf", "compliance-report.pdf", "Building the compliance report…",
      "Compliance report downloaded and filed under 03 — Validation Reports"],
    xlsx: ["components.xlsx", "component-list.xlsx", "Building the component list…",
      "Component list downloaded and filed under 02 — Design Data"],
    step: ["model.step", "model.step", "Writing the STEP file from the design model…",
      "STEP downloaded and filed under 02 — Design Data"],
    ga_dxf: ["ga.dxf", "general-arrangement.dxf", "Projecting the GA with hidden-line removal…",
      "GA (DXF) downloaded and filed under 06 — Drawings"],
    ga_svg: ["ga.svg", "general-arrangement.svg", "Projecting the GA with hidden-line removal…",
      "GA (SVG) downloaded and filed under 06 — Drawings"],
  };

  const serverFile = async (kind) => {
    const [path, name, working, ok] = SERVER[kind];
    try {
      setBusy(true); setStep(working);
      const res = await api.get(`/engine/${projectId}/export/${path}`, { responseType: "blob" });
      download(res.data, `${stamp}_${name}`);
      toast.success(ok);
      refresh();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); setStep(null); }
  };

  if (!model) {
    return (
      <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-4" data-testid="exports-empty">
        <DownloadSimple size={20} className="mb-2 text-[var(--c-accent)]" />
        <p className="text-sm text-[var(--c-text)]">Nothing to export yet</p>
        <p className="mt-1 text-xs text-[var(--c-muted)]">
          Generate the design model from the spec, then the GLB, the standard views, the component
          list and the compliance report all come from that one model.
        </p>
      </div>
    );
  }

  const haveViews = (files?.views || []).filter((v) => !v.startsWith("check-"));
  const checkViews = (files?.views || []).filter((v) => v.startsWith("check-"));

  return (
    <div className="flex flex-col gap-3" data-testid="exports-panel">
      <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]">
        <div className="flex items-center gap-2 border-b border-[var(--c-border)] px-3 py-1.5">
          <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--c-muted)]">
            Exports · {meta.id} rev {meta.revision || "—"}
          </span>
          {step && <span className="ml-auto font-mono text-[10px] text-[var(--c-accent)]" data-testid="exports-step">{step}</span>}
        </div>

        <Row icon={CubeTransparent} title="3D model · glTF binary"
          note="Opens in any viewer — Blender, Navisworks, Windows 3D Viewer. The design model id, revision and specification reference travel inside the file."
          actions={<>
            <Btn data-testid="export-glb-full" disabled={busy} onClick={() => glb(true)}>Complete</Btn>
            <Btn data-testid="export-glb-shown" disabled={busy} onClick={() => glb(false)}>As shown</Btn>
          </>} />

        <Row icon={ImageIcon} title="Standard views · PNG"
          note={`Isometric, front, side and plan at 2,400 px with the dimensions baked in${compliance ? ", plus one framed view per failing check for the report" : ""}.`}
          actions={<Btn data-testid="export-views" disabled={busy} onClick={views}>Render</Btn>} />

        <Row icon={Camera} title="Capture this view"
          note="Exactly what is on screen now — camera, labels, dimensions and the selected part."
          actions={<Btn data-testid="export-capture" disabled={busy} onClick={snap}>Capture</Btn>} />

        <Row icon={Blueprint} title="General Arrangement · DXF / SVG"
          note="Front elevation, side elevation and plan projected from this model with hidden-line removal, dimensioned off the built geometry, on an A3 sheet with the title block. The same sheet the Drawing Studio GA now issues."
          actions={<>
            <Btn data-testid="export-ga-dxf" disabled={busy} onClick={() => serverFile("ga_dxf")}>DXF</Btn>
            <Btn data-testid="export-ga-svg" disabled={busy} onClick={() => serverFile("ga_svg")}>SVG</Btn>
          </>} />

        <Row icon={Cube} title="Solid model · STEP AP214"
          note="Real solids built from the same model — opens in SolidWorks, Inventor, NX or FreeCAD for the client's own checks."
          actions={<Btn data-testid="export-step" disabled={busy} onClick={() => serverFile("step")}>Download</Btn>} />

        <Row icon={FileXls} title="Component list · Excel"
          note="Every component with its clause and quantity, where each number came from, the inventory decisions, the deviations and the compliance matrix."
          actions={<Btn data-testid="export-xlsx" disabled={busy} onClick={() => serverFile("xlsx")}>Download</Btn>} />

        <Row icon={FilePdf} title="Compliance report · PDF"
          note={compliance
            ? `${compliance.status} — ${compliance.counts.PASS} passed, ${compliance.counts.FAIL} failed, ${compliance.counts["SPEC ISSUE"]} spec issue(s). ${checkViews.length ? `${checkViews.length} check view(s) will be embedded.` : "Render the views first to embed the geometry."}`
            : "The checks have not been run yet — they will run as the report is built."}
          actions={<Btn data-testid="export-pdf" disabled={busy} onClick={() => serverFile("pdf")}>Download</Btn>} />
      </div>

      {!!haveViews.length && (
        <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-3" data-testid="exports-view-gallery">
          <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-[var(--c-muted)]">
            Views on file {checkViews.length ? `· ${checkViews.length} check view(s)` : ""}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {haveViews.map((v) => (
              <figure key={v} className="m-0 overflow-hidden rounded-sm border border-[var(--c-border)]">
                <img data-testid={`exports-view-${v}`} alt={v} loading="lazy"
                  src={`${api.defaults.baseURL}/engine/${projectId}/views/${v}.png`}
                  className="block h-auto w-full" />
                <figcaption className="px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">{v}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      )}

      {!!files?.files?.length && (
        <div className="overflow-hidden rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]" data-testid="exports-filed">
          <div className="flex items-center gap-1.5 border-b border-[var(--c-border)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--c-muted)]">
            <FolderOpen size={12} /> Filed in the document store · {files.files.length}
          </div>
          {files.files.slice(0, 12).map((f) => (
            <div key={f.id} data-testid={`exports-filed-${f.file_type}`}
              className="flex items-center gap-2 border-b border-[var(--c-border)]/50 px-3 py-1.5 text-[11px] last:border-0">
              <span className="font-mono text-[10px] text-[var(--c-accent)]">{f.file_id}</span>
              <span className="min-w-0 flex-1 truncate text-[var(--c-text)]" title={f.filename}>{f.filename}</span>
              <span className="font-mono text-[9px] text-[var(--c-muted)]">
                v{f.current_version} · {KB(f.size_bytes || 0)} · {f.folder_path}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
